import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { nFormatter } from "../../../Helpers";
import { currencySymbolHtml } from "../../../Components/common/CurrencySymbol";
import {
  applyEnterpriseChartTheme,
  HC_CONTAINER_PROPS,
} from "../../ExDashboard/components/chartTheme";
import { MD, mdScrollbarSx } from "./mdTheme";
import { MdWidgetCard } from "./MdCardShell";
import { MdCurrencyHint } from "./MdMoneyValue";

const SEGMENTS = [
  { key: "total", labelKey: "maintenance", color: MD.chart[0] },
  { key: "day", labelKey: "perDay", color: MD.chart[4] },
  { key: "km", labelKey: "perKm", color: MD.chart[2] },
];

const ROW_PX = 28;
const CHART_PAD = 48;
const VISIBLE_ROWS = 10;

const toEntries = (source, mode) => {
  if (mode === "total") {
    const list = Array.isArray(source) ? source : [];
    return list
      .map((item) => ({
        name: item.plateNumber || item.name || "—",
        y: Number(item.SaleNetAmount ?? item.saleNetAmount ?? item.y) || 0,
        count: item.Count ?? item.count,
      }))
      .filter((d) => d.y > 0)
      .sort((a, b) => b.y - a.y)
      .slice(0, 20);
  }

  // API / cache may pass the full cms/19 payload { costPerDay, costPerKm }
  let src = source;
  if (
    src &&
    typeof src === "object" &&
    !Array.isArray(src) &&
    (src.costPerDay != null || src.costPerKm != null)
  ) {
    src = mode === "day" ? src.costPerDay : src.costPerKm;
  }

  if (!src) return [];
  if (Array.isArray(src)) {
    return src
      .map((item) => ({
        name: item.plateNumber || item.name || item.key || "—",
        y:
          Number(
            item.y ??
              item.value ??
              item.cost ??
              item.SaleNetAmount ??
              item.saleNetAmount,
          ) || 0,
      }))
      .filter((d) => d.y > 0)
      .sort((a, b) => b.y - a.y)
      .slice(0, 20);
  }

  return Object.keys(src)
    .map((key) => ({ name: key, y: Number(src[key]) || 0 }))
    .filter((d) => d.y > 0)
    .sort((a, b) => b.y - a.y)
    .slice(0, 20);
};

const TopVehiclesCard = ({
  translate,
  loading,
  topVehicles,
  topPerDay,
  topPerKm,
  loginUserCurrency,
}) => {
  const [segment, setSegment] = useState("total");
  const [sortDir, setSortDir] = useState("desc");
  const activeSeg = SEGMENTS.find((s) => s.key === segment) || SEGMENTS[0];

  const points = useMemo(() => {
    const base =
      segment === "day"
        ? toEntries(topPerDay, "day")
        : segment === "km"
          ? toEntries(topPerKm, "km")
          : toEntries(topVehicles, "total");
    // Highcharts bar: first category is at the top of the chart.
    return [...base].sort((a, b) =>
      sortDir === "asc" ? a.y - b.y : b.y - a.y,
    );
  }, [segment, topVehicles, topPerDay, topPerKm, sortDir]);

  const titleMap = {
    total: translate("byMaintenance"),
    day: translate("byCostPerDay"),
    km: translate("byCostPerKm"),
  };

  const chartHeight = Math.max(
    VISIBLE_ROWS * ROW_PX + CHART_PAD,
    points.length * ROW_PX + CHART_PAD,
  );
  const viewportHeight = Math.min(
    chartHeight,
    VISIBLE_ROWS * ROW_PX + CHART_PAD,
  );

  const options = useMemo(() => {
    if (!points.length) return null;
    const cats = points.map((p) => p.name);
    const data = points.map((p) => p.y);

    return applyEnterpriseChartTheme({
      chart: {
        type: "bar",
        height: chartHeight,
        backgroundColor: "transparent",
        spacing: [8, 16, 8, 8],
      },
      title: { text: null },
      xAxis: {
        categories: cats,
        title: { text: null },
        lineWidth: 0,
        tickLength: 0,
        labels: {
          style: {
            color: MD.fg,
            fontSize: "11px",
            fontWeight: "500",
          },
        },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: MD.line2,
        gridLineWidth: 1,
        labels: {
          style: { color: MD.muted, fontSize: "10px", fontWeight: "500" },
          formatter() {
            return nFormatter(this.value, 1);
          },
        },
      },
      tooltip: {
        formatter() {
          const point = points.find((p) => p.name === this.key);
          let html =
            "<b>Vehicle: </b>" +
            this.key +
            "<br/><b>Value: </b>" +
            Number(this.y).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) +
            currencySymbolHtml(loginUserCurrency);
          if (point?.count != null) {
            html += "<br/><b>Maint Count: </b>" + point.count;
          }
          return html;
        },
      },
      plotOptions: {
        bar: {
          color: activeSeg.color,
          borderWidth: 0,
          borderRadius: 4,
          pointPadding: 0.08,
          groupPadding: 0.06,
          dataLabels: {
            enabled: true,
            inside: false,
            useHTML: true,
            style: {
              color: MD.fg,
              fontSize: "10px",
              fontWeight: "600",
              textOutline: "none",
            },
            formatter() {
              return (
                nFormatter(this.y, 1) + currencySymbolHtml(loginUserCurrency)
              );
            },
          },
        },
      },
      series: [
        {
          name: translate(activeSeg.labelKey),
          showInLegend: false,
          data,
          color: activeSeg.color,
        },
      ],
      credits: { enabled: false },
      legend: { enabled: false },
    });
  }, [points, loginUserCurrency, activeSeg, chartHeight, translate]);

  const chipSx = (active) => ({
    textTransform: "none",
    fontSize: 12,
    fontWeight: 500,
    minHeight: 28,
    px: 1.5,
    borderRadius: "4px",
    border: `1px solid ${MD.link}`,
    bgcolor: active ? MD.link : "#fff",
    color: active ? "#fff" : MD.link,
    "&:hover": {
      bgcolor: active ? MD.link : "#f5f8ff",
      borderColor: MD.link,
    },
  });

  return (
    <MdWidgetCard
      loading={loading}
      eyebrow={
        <>
          {translate("top20Vehicles") || "Top 20 Vehicles"}
          <MdCurrencyHint currency={loginUserCurrency} />
        </>
      }
      infoTitle={translate("mdTopVehiclesInfo")}
      title={titleMap[segment]}
    >
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={0.75}
        sx={{ mb: 1.5, alignItems: "center" }}
      >
        {SEGMENTS.map((seg) => (
          <Button
            key={seg.key}
            type="button"
            size="small"
            onClick={() => setSegment(seg.key)}
            disableElevation
            sx={chipSx(segment === seg.key)}
          >
            {translate(seg.labelKey)}
          </Button>
        ))}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Tooltip title={translate("sortDesc") || "DESC"}>
          <IconButton
            type="button"
            size="small"
            aria-label={translate("sortDesc") || "DESC"}
            onClick={() => setSortDir("desc")}
            sx={{
              ...chipSx(sortDir === "desc"),
              minWidth: 28,
              width: 28,
              height: 28,
              p: 0,
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={translate("sortAsc") || "ASC"}>
          <IconButton
            type="button"
            size="small"
            aria-label={translate("sortAsc") || "ASC"}
            onClick={() => setSortDir("asc")}
            sx={{
              ...chipSx(sortDir === "asc"),
              minWidth: 28,
              width: 28,
              height: 28,
              p: 0,
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          height: viewportHeight,
          maxHeight: viewportHeight,
          width: "100%",
          overflowX: "hidden",
          overflowY: chartHeight > viewportHeight ? "auto" : "hidden",
          overscrollBehavior: "contain",
          ...mdScrollbarSx,
        }}
      >
        {options ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{
              ...HC_CONTAINER_PROPS,
              style: {
                height: chartHeight,
                width: "100%",
              },
            }}
            immutable
          />
        ) : null}
      </Box>
    </MdWidgetCard>
  );
};

export default TopVehiclesCard;
